import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { View, ViewStyle } from 'react-native'
import { Svg, Circle as SvgCircle, Path as SvgPath } from 'react-native-svg'

export type CountdownTimerRef = {
  resetTimer: (durationMs?: number, startImmediately?: boolean) => void
  pauseTimer: () => void
  resumeTimer: () => void
  getRemaining: () => number
  isRunning: () => boolean
}

type Props = {
  initialTimerDuration?: number
  startAutomatically?: boolean
  onTimerEnd?: () => void
  onTick?: (remainingMs: number) => void

  // visuals
  width?: number
  height?: number
  strokeColor?: string
  strokeWidth?: number
  foregroundColor?: string
  backgroundColor?: string
  clockwise?: boolean
  containerStyle?: ViewStyle
}

const CircularCountdownTimer = forwardRef<CountdownTimerRef, Props>(({
  initialTimerDuration = 30_000,
  startAutomatically = true,
  onTimerEnd,
  onTick,
  width = 20,
  height = 20,
  strokeColor = '#000',
  strokeWidth = 1,
  foregroundColor = '#000',
  backgroundColor = 'transparent',
  clockwise = true,
  containerStyle
}: Props, ref) => {
  const [durationMs, setDurationMs] = useState<number>(initialTimerDuration)
  const [remainingMs, setRemainingMs] = useState<number>(initialTimerDuration)
  const [running, setRunning] = useState<boolean>(startAutomatically)
  const endTimeRef = useRef<number>(startAutomatically ? (Date.now() + initialTimerDuration) : 0)

  useImperativeHandle(ref, () => ({
    resetTimer: (d?: number, startNow = false) => {
      const next = typeof d === 'number' ? d : durationMs
      setDurationMs(next)
      setRemainingMs(next)
      endTimeRef.current = Date.now() + next
      setRunning(!!startNow)
    },
    pauseTimer: () => {
      setRunning(false)
    },
    resumeTimer: () => {
      if (remainingMs <= 0) return
      endTimeRef.current = Date.now() + remainingMs
      setRunning(true)
    },
    getRemaining: () => remainingMs,
    isRunning: () => running
  }), [durationMs, remainingMs, running])

  useEffect(() => {
    if (!running) return

    const id = setInterval(() => {
      const rem = endTimeRef.current - Date.now()
      if (rem <= 0) {
        setRemainingMs(0)
        setRunning(false)
        onTick?.(0)
        onTimerEnd?.()
        clearInterval(id)
      } else {
        setRemainingMs(rem)
        onTick?.(rem)
      }
    }, 100)

    return () => clearInterval(id)
  }, [running])

  useEffect(() => {
    if (startAutomatically) {
      endTimeRef.current = Date.now() + initialTimerDuration
      setRemainingMs(initialTimerDuration)
      setRunning(true)
    } else {
      setRunning(false)
      setRemainingMs(initialTimerDuration)
    }
  }, [])

  const progress = Math.max(0, Math.min(1, remainingMs / Math.max(1, durationMs)))

  const pathD = useMemo(() => {
    const size = { w: width, h: height }
    const r = Math.max(1, (Math.min(size.w, size.h) / 2) - (strokeWidth / 2))
    const cx = size.w / 2
    const cy = size.h / 2
    const startAngle = -Math.PI / 2
    const sign = clockwise ? -1 : 1
    const endAngle = startAngle + (sign * 2 * Math.PI * progress)

    if (progress >= 0.999) {
      return { full: true, d: '' }
    }
    if (progress <= 0.001) {
      return { full: false, d: '' }
    }

    const cosStart = Math.cos(startAngle)
    const sinStart = Math.sin(startAngle)
    const cosEnd = Math.cos(endAngle)
    const sinEnd = Math.sin(endAngle)
    const sx = cx + (r * cosStart)
    const sy = cy + (r * sinStart)
    const ex = cx + (r * cosEnd)
    const ey = cy + (r * sinEnd)
    const largeArc = progress > 0.5 ? 1 : 0
    const sweepFlag = clockwise ? 0 : 1

    const d = [
      `M ${cx} ${cy}`,
      `L ${sx} ${sy}`,
      `A ${r} ${r} 0 ${largeArc} ${sweepFlag} ${ex} ${ey}`,
      'Z'
    ].join(' ')

    return { full: false, d }
  }, [progress, width, height, strokeWidth, clockwise])

  return (
    <View style={containerStyle}>
      <Svg width={width} height={height}>
        <SvgCircle
          cx={width / 2}
          cy={height / 2}
          r={Math.max(1, (Math.min(width, height) / 2) - (strokeWidth / 2))}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill={backgroundColor}
        />
        {pathD.full ? (
          <SvgCircle
            cx={width / 2}
            cy={height / 2}
            r={Math.max(1, (Math.min(width, height) / 2) - (strokeWidth / 2))}
            fill={foregroundColor}
          />
        ) : pathD.d ? (
          <SvgPath
            d={pathD.d}
            fill={foregroundColor}
          />
        ) : null}
      </Svg>
    </View>
  )
})

CircularCountdownTimer.displayName = 'CircularCountdownTimer'

export default CircularCountdownTimer


